import { type ComponentType, memo } from 'react';
import * as THREE from 'three';
import { FUTURE, INDUSTRIAL, MODERN } from './materials';
import { gableRoof, type ModelProps, Pole } from './parts';

/**
 * City sprawl: the cosmetic tiles a city spreads over from L5 (see
 * `SPRAWL_STAGES`). Everything stays low (under 0.3) so the city itself reads
 * as the landmark, and each era has a few variants picked by `variant`.
 * Props keep out of r ≈ 0.2 around the tile centre and nothing tall stands in
 * front (+z, towards the camera) of it: sprawl is on the ring beside the city,
 * where units spawn, unload and deploy, and they stand at the centre.
 */

const { BRICK, DARK_BRICK, IRON, SLATE, COBBLE, SOOT } = INDUSTRIAL;
const { CONCRETE, DARK_CONCRETE, GLASS, ASPHALT, ROAD_LINE, WINDOW, GREEN_LEAF } = MODERN;
const { HULL, PANEL, DOME, GARDEN } = FUTURE;

export type SprawlProps = ModelProps & {
  /** Which of the era's variants to draw (any integer; wraps). */
  variant: number;
};

const G = {
  box: new THREE.BoxGeometry(1, 1, 1),
  pad: new THREE.CylinderGeometry(0.72, 0.72, 0.004, 6),
  // Industrial
  row: new THREE.BoxGeometry(0.46, 0.11, 0.13),
  rowRoof: gableRoof(0.085, 0.48),
  rowStack: new THREE.BoxGeometry(0.025, 0.05, 0.025),
  workshop: new THREE.BoxGeometry(0.34, 0.12, 0.26),
  // Sawtooth roof teeth run along x.
  tooth: gableRoof(0.07, 0.36),
  shopChimney: new THREE.CylinderGeometry(0.022, 0.028, 0.24, 6),
  crate: new THREE.BoxGeometry(0.06, 0.06, 0.06),
  lampHead: new THREE.BoxGeometry(0.03, 0.03, 0.03),
  // Modern
  laneLine: new THREE.BoxGeometry(1, 0.004, 0.012),
  treeTop: new THREE.IcosahedronGeometry(0.06, 0),
  // Future
  dome: new THREE.SphereGeometry(1, 16, 8, 0, Math.PI * 2, 0, Math.PI / 2),
  domeRim: new THREE.TorusGeometry(1, 0.06, 4, 24).rotateX(Math.PI / 2),
  lightPad: new THREE.CylinderGeometry(0.1, 0.1, 0.012, 6),
  gardenRing: new THREE.TorusGeometry(0.3, 0.05, 4, 24).rotateX(Math.PI / 2),
  pathLight: new THREE.OctahedronGeometry(0.02, 0),
};

/**
 * Ground slabs are a thin pad covering the tile; props sit on top of it
 * (y = PAD). Thin enough to stay under `PlacementOverlay`'s tint (+0.01), as
 * sprawl tiles stay placeable.
 */
const PAD = 0.004;

function Pad({ material }: { material: THREE.Material }) {
  return <mesh geometry={G.pad} material={material} position={[0, PAD / 2, 0]} />;
}

/** A box `w` × `h` × `d` standing on the pad at (x, z). */
function Block({
  x,
  z,
  w,
  h,
  d,
  material,
}: {
  x: number;
  z: number;
  w: number;
  h: number;
  d: number;
  material: THREE.Material;
}) {
  return (
    <mesh geometry={G.box} material={material} position={[x, PAD + h / 2, z]} scale={[w, h, d]} />
  );
}

// ---------------------------------------------------------------- Industrial

/** A brick terrace along x under an iron (or team) roof. */
function Terrace({ x, z, roof }: { x: number; z: number; roof: THREE.Material }) {
  return (
    <group position={[x, PAD, z]}>
      <mesh geometry={G.row} material={BRICK} position={[0, 0.055, 0]} />
      <mesh geometry={G.rowRoof} material={roof} position={[0, 0.15, 0]} />
      {[-0.15, 0, 0.15].map((sx) => (
        <mesh key={sx} geometry={G.rowStack} material={DARK_BRICK} position={[sx, 0.2, -0.02]} />
      ))}
    </group>
  );
}

/** A sawtooth-roofed brick workshop with a chimney and crates in its yard. */
function Workshop({ x, z, accent }: { x: number; z: number; accent: THREE.Material }) {
  return (
    <group position={[x, PAD, z]}>
      <mesh geometry={G.workshop} material={DARK_BRICK} position={[0, 0.06, 0]} />
      {[-0.08, 0.02, 0.12].map((tz, i) => (
        <mesh
          key={tz}
          geometry={G.tooth}
          material={i === 1 ? accent : SLATE}
          position={[0, 0.15, tz - 0.02]}
          scale={[1, 1, 0.8]}
        />
      ))}
      <mesh geometry={G.shopChimney} material={DARK_BRICK} position={[0.13, 0.12, -0.1]} />
      {[-0.08, 0].map((cx) => (
        <mesh key={cx} geometry={G.crate} material={SOOT} position={[cx, 0.03, 0.2]} />
      ))}
    </group>
  );
}

function GasLamp({ x, z, glow }: { x: number; z: number; glow: THREE.Material }) {
  return (
    <group position={[x, PAD, z]}>
      <Pole length={0.2} position={[0, 0.1, 0]} material={IRON} />
      <mesh geometry={G.lampHead} material={glow} position={[0, 0.21, 0]} />
    </group>
  );
}

const INDUSTRIAL_VARIANTS: readonly ComponentType<ModelProps>[] = [
  // Two terraces across the tile, one under a team roof.
  ({ accent, glow }) => (
    <>
      <Terrace x={0} z={-0.3} roof={IRON} />
      <Terrace x={0} z={0.34} roof={accent} />
      <GasLamp x={0.34} z={-0.02} glow={glow} />
    </>
  ),
  // A workshop with a terrace in front of it.
  ({ accent }) => (
    <>
      <Workshop x={-0.02} z={-0.3} accent={accent} />
      <Terrace x={0.05} z={0.34} roof={IRON} />
    </>
  ),
  // A short terrace and a workshop side by side, with a lamp.
  ({ accent, glow }) => (
    <>
      <group rotation={[0, Math.PI / 3, 0]}>
        <Terrace x={0} z={-0.3} roof={accent} />
      </group>
      <Workshop x={0.12} z={-0.3} accent={accent} />
      <GasLamp x={-0.4} z={0.1} glow={glow} />
    </>
  ),
];

/** L5 Industrial sprawl: brick terraces under iron roofs and workshops on cobbles. */
function IndustrialSprawl(props: SprawlProps) {
  const Variant = pick(INDUSTRIAL_VARIANTS, props.variant);
  return (
    <>
      <Pad material={COBBLE} />
      <Variant {...props} />
    </>
  );
}

// ------------------------------------------------------------------ Modern

/** A concrete or glass block with lit window strips and a flat roof. */
function OfficeBlock({
  x,
  z,
  w,
  d,
  h,
  body,
  roof,
}: {
  x: number;
  z: number;
  w: number;
  d: number;
  h: number;
  body: THREE.Material;
  roof: THREE.Material;
}) {
  const strips = [0.3, 0.6, 0.85].map((f) => f * h).filter((y) => y > 0.05);
  return (
    <>
      <Block x={x} z={z} w={w} h={h} d={d} material={body} />
      {strips.map((y) => (
        <mesh
          key={y}
          geometry={G.box}
          material={WINDOW}
          position={[x, PAD + y, z]}
          scale={[w + 0.006, 0.014, d + 0.006]}
        />
      ))}
      <Block x={x} z={z} w={w * 0.9} h={h + 0.02} d={d * 0.9} material={roof} />
    </>
  );
}

/** A road across the tile along x at `z`, with a centre line. */
function Road({ z, yaw = 0 }: { z: number; yaw?: number }) {
  return (
    <group rotation={[0, yaw, 0]}>
      <mesh
        geometry={G.box}
        material={ASPHALT}
        position={[0, PAD + 0.003, z]}
        scale={[1.5, 0.006, 0.16]}
      />
      <mesh
        geometry={G.laneLine}
        material={ROAD_LINE}
        position={[0, PAD + 0.008, z]}
        scale={[1.5, 1, 1]}
      />
    </group>
  );
}

const PARKING_BAYS = [-0.24, -0.12, 0, 0.12, 0.24];
const PARK_TREES: [number, number][] = [
  [-0.3, 0.2],
  [-0.12, 0.34],
  [-0.36, -0.02],
];

const MODERN_VARIANTS: readonly ComponentType<ModelProps>[] = [
  // Two office blocks either side of a road.
  ({ accent }) => (
    <>
      <Road z={0} />
      <OfficeBlock x={-0.14} z={-0.3} w={0.34} d={0.22} h={0.26} body={CONCRETE} roof={accent} />
      <OfficeBlock
        x={0.3}
        z={0.38}
        w={0.3}
        d={0.18}
        h={0.14}
        body={DARK_CONCRETE}
        roof={CONCRETE}
      />
    </>
  ),
  // A glass block over a car park.
  ({ accent }) => (
    <>
      <Road z={0.08} yaw={Math.PI / 3} />
      <OfficeBlock x={0} z={-0.3} w={0.42} d={0.22} h={0.28} body={GLASS} roof={accent} />
      {PARKING_BAYS.map((x) => (
        <mesh
          key={x}
          geometry={G.laneLine}
          material={ROAD_LINE}
          position={[x, PAD + 0.004, 0.36]}
          rotation={[0, Math.PI / 2, 0]}
          scale={[0.14, 1, 1]}
        />
      ))}
    </>
  ),
  // A pocket park beside a low block.
  ({ accent }) => (
    <>
      <mesh
        geometry={G.box}
        material={GREEN_LEAF}
        position={[-0.22, PAD + 0.005, 0.12]}
        scale={[0.4, 0.01, 0.5]}
      />
      {PARK_TREES.map(([x, z]) => (
        <group key={x} position={[x, PAD, z]}>
          <Pole length={0.08} position={[0, 0.04, 0]} material={DARK_CONCRETE} />
          <mesh geometry={G.treeTop} material={GREEN_LEAF} position={[0, 0.11, 0]} />
        </group>
      ))}
      <OfficeBlock x={0.3} z={-0.24} w={0.26} d={0.3} h={0.22} body={CONCRETE} roof={accent} />
    </>
  ),
];

/** L6 Modern sprawl: concrete and glass blocks with lit windows, roads and parks on asphalt. */
function ModernSprawl(props: SprawlProps) {
  const Variant = pick(MODERN_VARIANTS, props.variant);
  return (
    <>
      <Pad material={DARK_CONCRETE} />
      <Variant {...props} />
    </>
  );
}

// ------------------------------------------------------------------ Future

/** A glass dome over a garden floor, on a team rim. */
function Dome({
  x,
  z,
  size,
  accent,
}: {
  x: number;
  z: number;
  size: number;
  accent: THREE.Material;
}) {
  return (
    <group position={[x, PAD, z]} scale={size}>
      <mesh
        geometry={G.box}
        material={GARDEN}
        position={[0, 0.005 / size, 0]}
        scale={[1.4, 0.01 / size, 1.4]}
      />
      <mesh geometry={G.dome} material={DOME} />
      <mesh geometry={G.domeRim} material={accent} />
    </group>
  );
}

function LightPad({ x, z, glow }: { x: number; z: number; glow: THREE.Material }) {
  return <mesh geometry={G.lightPad} material={glow} position={[x, PAD + 0.006, z]} />;
}

const PATH_LIGHTS = [-0.36, -0.18, 0, 0.18, 0.36];

const FUTURE_VARIANTS: readonly ComponentType<ModelProps>[] = [
  // Two domes and a landing pad.
  ({ accent, glow }) => (
    <>
      <Dome x={-0.28} z={-0.26} size={0.18} accent={accent} />
      <Dome x={0.36} z={0} size={0.14} accent={accent} />
      <LightPad x={-0.1} z={0.34} glow={glow} />
    </>
  ),
  // A big dome behind a glowing path.
  ({ accent, glow }) => (
    <>
      <Dome x={0} z={-0.4} size={0.18} accent={accent} />
      {PATH_LIGHTS.map((x) => (
        <mesh key={x} geometry={G.pathLight} material={glow} position={[x, PAD + 0.03, 0.34]} />
      ))}
    </>
  ),
  // A garden ring round an open plaza, a small dome, a hull block and a pad.
  ({ accent, glow }) => (
    <>
      <mesh geometry={G.gardenRing} material={GARDEN} position={[0, PAD + 0.01, 0]} />
      <Dome x={0.26} z={-0.4} size={0.12} accent={accent} />
      <Block x={-0.42} z={-0.26} w={0.16} h={0.14} d={0.16} material={HULL} />
      <LightPad x={0.42} z={0.26} glow={glow} />
    </>
  ),
];

/** L7 Future sprawl: garden domes, hull blocks and glowing pads on white panels. */
function FutureSprawl(props: SprawlProps) {
  const Variant = pick(FUTURE_VARIANTS, props.variant);
  return (
    <>
      <Pad material={PANEL} />
      <Variant {...props} />
    </>
  );
}

function pick<T>(variants: readonly T[], variant: number): T {
  const n = variants.length;
  return variants[((Math.trunc(variant) % n) + n) % n];
}

/**
 * Sprawl look by city level. Levels below the first key draw nothing (cities
 * only sprawl from L5); levels past the last use the last entry.
 */
const SPRAWL_STAGES: Readonly<Record<number, ComponentType<SprawlProps>>> = {
  5: IndustrialSprawl,
  6: ModernSprawl,
  7: FutureSprawl,
};

/** How many variants each sprawl level has (for the gallery). */
export const SPRAWL_VARIANTS: Readonly<Record<number, number>> = {
  5: INDUSTRIAL_VARIANTS.length,
  6: MODERN_VARIANTS.length,
  7: FUTURE_VARIANTS.length,
};

const SPRAWL_LEVELS = Object.keys(SPRAWL_STAGES)
  .map(Number)
  .sort((a, b) => a - b);

/** One sprawl tile of a city at `level`: nothing below the first sprawl level. */
export const SprawlTileModel = memo(function SprawlTileModel(props: SprawlProps) {
  if (props.level < SPRAWL_LEVELS[0]) return null;
  const level = Math.min(props.level, SPRAWL_LEVELS[SPRAWL_LEVELS.length - 1]);
  const Stage = SPRAWL_STAGES[level];
  return <Stage {...props} />;
});
